import { encode, useFormatter, useSettings } from '@capsicum/common';
import { useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useCallback, useMemo } from 'react';

import {
	wcPledgeBasketAtom,
	wcPledgeBasketDataAtom,
	wcPledgeBasketTotalPledgeAmountAtom,
	wcPledgeBasketTotalUSDAmountAtom
} from '../data-access/atoms/wcPledgeBasketAtoms';
import { type WCPledgeBasketItem } from '../models';

import { useWCPledgeRates } from './useWCPledgeRates';

export const useWCPledgeBasketState = () => {
	const { formatCrypto, formatFiat } = useFormatter();

	const { settings } = useSettings();
	const { chzBalance, usdRate } = useWCPledgeRates();

	const { lockerRoomInternationalsConfig } = settings;
	const { maxTeamsPerPledge } = lockerRoomInternationalsConfig;

	const setWCPledgeBasket = useSetAtom(wcPledgeBasketAtom);
	const totalPledgeAmount = useAtomValue(wcPledgeBasketTotalPledgeAmountAtom);
	const totalPledgeAmountUSD = useAtomValue(wcPledgeBasketTotalUSDAmountAtom);

	const emptyBasket = useCallback(() => {
		setWCPledgeBasket(RESET);
	}, [setWCPledgeBasket]);

	const addPledgeToBasket = useCallback(
		(teamId: string, ftName: string) => {
			setWCPledgeBasket(prevRaw => {
				const prev = prevRaw as Array<WCPledgeBasketItem>;

				return prev.concat({
					amount: lockerRoomInternationalsConfig?.minimumCHZAmountToStake ?? 0,
					ftName,
					teamId,
					usdAmount: (lockerRoomInternationalsConfig?.minimumCHZAmountToStake ?? 0) * (usdRate ?? 0)
				});
			});
		},
		[setWCPledgeBasket, lockerRoomInternationalsConfig, usdRate]
	);

	const removePledgeFromBasket = useCallback(
		(teamId: string) => {
			setWCPledgeBasket(prevRaw => {
				const prev = prevRaw as Array<WCPledgeBasketItem>;

				return prev.filter(team => team.teamId !== teamId);
			});
		},
		[setWCPledgeBasket]
	);

	const updatePledgeAmount = useCallback(
		(teamId: string, newAmount: number) => {
			setWCPledgeBasket(prevRaw => {
				const prev = prevRaw as Array<WCPledgeBasketItem>;

				return prev.map(team =>
					team.teamId === teamId
						? { ...team, amount: newAmount, usdAmount: newAmount * (usdRate ?? 0) }
						: team
				);
			});
		},
		[setWCPledgeBasket, usdRate]
	);

	const toggleBasketSelection = useCallback(
		(teamId: string, ftName: string) => {
			setWCPledgeBasket(prevRaw => {
				const prev = prevRaw as Array<WCPledgeBasketItem>;
				const exists = prev.find(team => team.teamId === teamId);

				if (!exists) {
					if (prev.length >= maxTeamsPerPledge) {
						return prev;
					}

					return prev.concat({
						amount: lockerRoomInternationalsConfig?.minimumCHZAmountToStake ?? 0,
						ftName,
						teamId,
						usdAmount: (lockerRoomInternationalsConfig?.minimumCHZAmountToStake ?? 0) * (usdRate ?? 0)
					});
				}

				return prev.filter(team => team.teamId !== teamId);
			});
		},
		[setWCPledgeBasket, lockerRoomInternationalsConfig, usdRate, maxTeamsPerPledge]
	);
	const formattedTotalPledgeAmount = useMemo(
		() => formatCrypto(totalPledgeAmount, 'CHZ'),
		[formatCrypto, totalPledgeAmount]
	);

	const formattedTotalPledgeAmountUSD = useMemo(
		() => formatCrypto(totalPledgeAmountUSD, 'USD'),
		[formatFiat, totalPledgeAmountUSD]
	);

	const formattedTotalPledgeAmountUSDWithDoubleTilde = useMemo(
		() => `${encode('doubleTilde')}${formattedTotalPledgeAmountUSD}`,
		[formattedTotalPledgeAmountUSD]
	);

	const insufficientBalance: boolean = (chzBalance ?? 0) < totalPledgeAmount;

	const basket = useAtomValue(wcPledgeBasketDataAtom);

	const isAnyAmountInputBelowDefaultPledgeAmount = useMemo(() => {
		const minAmount = lockerRoomInternationalsConfig?.minimumCHZAmountToStake ?? 0;

		return basket.some(team => team.amount < minAmount);
	}, [basket, lockerRoomInternationalsConfig]);

	return {
		addPledgeToBasket,
		emptyBasket,
		formattedTotalPledgeAmount,
		formattedTotalPledgeAmountUSD,
		formattedTotalPledgeAmountUSDWithDoubleTilde,
		insufficientBalance,
		isAnyAmountInputBelowDefaultPledgeAmount,
		maxTeamsPerPledge,
		removePledgeFromBasket,
		toggleBasketSelection,
		updatePledgeAmount
	};
};
